import { IFluidObject } from '@fluidframework/core-interfaces';
import { FindIframe } from '../Shared/FindIframe';
import { PostMessageHandlersCollection, PostMessageSender } from '../Shared/PostMessage';
import { getHostSideFocusable } from '../Proxies/FocusableProxy';
import { getHostSideProduceCommands } from '../Proxies/ProduceCommandsProxy';
import { getHostSideComponentTitleHandler } from '../Proxies/ComponentTitleHandlerProxy';
import { getHostSideLastEdited } from '../Proxies/LastEditedProxy';
import { getHostSideComponentProvidePresence } from '../Proxies/ComponentProvidePresenceProxy';
import { getHostSideComponentCanvasProvider } from '../Proxies/ComponentCanvasProviderProxy';
import { getHostSideMeetingInfo } from '../Proxies/MeetingInfoProxy';
import { getHostSideKeyboardShortcutEmitter } from '../Proxies/KeyboardShortcutEmitterProxy';
import { getHostSideFluidLoadable } from '../Proxies/FluidLoadableProxy';
import { getHostSideComponentMeetingInformationProvider } from '../Proxies/ComponentMeetingInformationProviderProxy';
import { getHostSideMessageBannerProvider } from '../Proxies/MessageBannerProviderProxy';

/**
 * Initialize behaviors for all known component interfaces
 * @param iframeInfo
 */
export function getComponentInterfaceInfo(
  findIframe: FindIframe,
  sender: PostMessageSender,
  componentId: string,
  handlersCollection: PostMessageHandlersCollection<string>
): { component: IFluidObject; dispose: () => void } {
  const hostSideFocusable = getHostSideFocusable(sender, findIframe);
  const hostSideProduceCommands = getHostSideProduceCommands(sender);
  const hostSideComponentTitleHandler = getHostSideComponentTitleHandler(sender);
  const hostSideLastEdited = getHostSideLastEdited();
  const hostSideComponentProvidePresence = getHostSideComponentProvidePresence();
  const hostSideComponentCanvasProvider = getHostSideComponentCanvasProvider(sender, findIframe, handlersCollection);
  const hostSideMeetingInfo = getHostSideMeetingInfo(sender);
  const hostSideKeyboardShortcutEmitter = getHostSideKeyboardShortcutEmitter(sender);
  const hostSideFluidLoadable = getHostSideFluidLoadable();
  const hostSideComponentMeetingInformationProvider = getHostSideComponentMeetingInformationProvider(sender);
  const hostSideMessageBannerProvider = getHostSideMessageBannerProvider(sender);
  const component = {
    ComponentFocusable: hostSideFocusable.component,
    ComponentProduceCommands: hostSideProduceCommands.component,
    ComponentTitleHandler: hostSideComponentTitleHandler.component,
    IComponentLastEditedTracker: hostSideLastEdited.component,
    IComponentProvidePresence: hostSideComponentProvidePresence.component,
    ComponentCanvasProvider: hostSideComponentCanvasProvider.component,
    MeetingInfo: hostSideMeetingInfo.component,
    KeyboardShortcutEmitter: hostSideKeyboardShortcutEmitter.component,
    IFluidLoadable: hostSideFluidLoadable.component,
    ComponentMeetingInformationProvider: hostSideComponentMeetingInformationProvider.component,
    MessageBannerProvider: hostSideMessageBannerProvider.component
  };

  handlersCollection[componentId] = {
    ...handlersCollection[componentId],
    ...hostSideFocusable.handlers,
    ...hostSideProduceCommands.handlers,
    ...hostSideComponentTitleHandler.handlers,
    ...hostSideLastEdited.handlers,
    ...hostSideComponentProvidePresence.handlers,
    ...hostSideKeyboardShortcutEmitter.handlers,
    ...hostSideFluidLoadable.handlers,
    ...hostSideMessageBannerProvider.handlers
  };

  return {
    component,
    dispose: () => {
      hostSideProduceCommands.dispose();
      hostSideComponentProvidePresence.dispose();
      hostSideComponentTitleHandler.dispose();
      hostSideLastEdited.dispose();
      hostSideKeyboardShortcutEmitter.dispose();
      hostSideMessageBannerProvider.dispose();
    }
  };
}
